import React, { useEffect, useState } from 'react';
import { Linkedin, Twitter } from 'lucide-react';
import api from './api';

const AVATAR_COLORS = [
  ['#E8611A','#fff7ed'],
  ['#3b82f6','#eff6ff'],
  ['#16a34a','#f0fdf4'],
  ['#7c3aed','#f5f3ff'],
  ['#0891b2','#ecfeff'],
];

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).slice(0,2).join('').toUpperCase();
};

/* Inject keyframes once */
const STYLE_ID = 'team-marquee-style';
if (!document.getElementById(STYLE_ID)) {
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    @keyframes marquee {
      0%   { transform: translateX(0); }
      100% { transform: translateX(-50%); }
    }
    .marquee-track {
      display: flex;
      width: max-content;
      animation: marquee 45s linear infinite;
    }
    .marquee-track:hover {
      animation-play-state: paused;
    }
  `;
  document.head.appendChild(style);
}

const TeamMember = ({ member, colorIndex }) => {
  const [hovered, setHovered] = useState(false);
  const [accent, bg] = AVATAR_COLORS[colorIndex % AVATAR_COLORS.length];

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => member.profile_link && window.open(member.profile_link, '_blank')}
      style={{
        width: 240, flexShrink: 0, margin: '0 10px',
        background: '#fff',
        borderRadius: 18, overflow: 'hidden',
        boxShadow: hovered ? '0 16px 40px rgba(0,0,0,0.13)' : '0 3px 14px rgba(0,0,0,0.07)',
        border: hovered ? `1.5px solid ${accent}35` : '1.5px solid rgba(0,0,0,0.06)',
        transition: 'transform .25s, box-shadow .25s, border-color .25s',
        transform: hovered ? 'translateY(-6px)' : 'none',
        cursor: member.profile_link ? 'pointer' : 'default',
      }}
    >
      {/* Image / avatar */}
      <div style={{
        height: 190, position: 'relative', overflow: 'hidden',
        background: member.image ? `url(${member.image}) center/cover no-repeat` : `linear-gradient(135deg,${bg} 0%,${accent}18 100%)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {!member.image && (
          <div style={{ width:72, height:72, borderRadius:'50%', background:accent, display:'flex', alignItems:'center', justifyContent:'center', boxShadow:`0 4px 20px ${accent}50`, fontSize:'1.5rem', fontWeight:800, color:'#fff' }}>
            {getInitials(member.nom_prenom)}
          </div>
        )}
        <div style={{ position:'absolute', inset:0, background:`linear-gradient(to top,${accent}cc 0%,transparent 55%)`, opacity:hovered?1:0, transition:'opacity .3s' }} />
        <div style={{ position:'absolute', bottom:12, left:0, width:'100%', display:'flex', justifyContent:'center', gap:8, opacity:hovered?1:0, transition:'opacity .3s' }}>
          {member.linkedin && (
            <a href={member.linkedin} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()}
              style={{ width:32, height:32, borderRadius:'50%', background:'rgba(255,255,255,0.95)', display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none' }}>
              <Linkedin size={14} color="#0A66C2" />
            </a>
          )}
          {member.twitter && (
            <a href={member.twitter} target="_blank" rel="noopener noreferrer" onClick={e=>e.stopPropagation()}
              style={{ width:32, height:32, borderRadius:'50%', background:'rgba(255,255,255,0.95)', display:'flex', alignItems:'center', justifyContent:'center', textDecoration:'none' }}>
              <Twitter size={14} color="#1DA1F2" />
            </a>
          )}
        </div>
      </div>

      {/* Infos */}
      <div style={{ padding:'14px 16px 18px', fontFamily:"'DM Sans',sans-serif" }}>
        <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:3 }}>
          <span style={{ width:7, height:7, borderRadius:'50%', background:accent, flexShrink:0 }} />
          <h3 style={{ fontSize:'0.9rem', fontWeight:700, color:'#1e293b', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {member.nom_prenom}
          </h3>
        </div>
        <p style={{ fontSize:'0.76rem', color:accent, fontWeight:600, margin:'0 0 0 14px', letterSpacing:'0.03em' }}>
          {member.role}
        </p>
      </div>
    </div>
  );
};

const TeamSection = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/aydees/liste-equipe/')
      .then(res => setTeamMembers(res.data))
      .catch(err => console.error('Erreur:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ padding:'64px 0', background:'#F8F7F4', display:'flex', justifyContent:'center', alignItems:'center', minHeight:340 }}>
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500" />
    </div>
  );

  if (!teamMembers || teamMembers.length === 0) return null;

  /* Duplicate list so CSS animation loops seamlessly */
  const doubled = [...teamMembers, ...teamMembers];

  return (
    <section style={{ background:'#F8F7F4', padding:'36px 0 32px', fontFamily:"'DM Sans',sans-serif", borderBottom:'1px solid rgba(0,0,0,0.07)' }}>
      <div style={{ maxWidth:1160, margin:'0 auto', padding:'0 24px', overflow:'hidden', position:'relative' }}>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:20 }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 16px', borderRadius:999, border:'1px solid rgba(0,0,0,0.1)', background:'rgba(0,0,0,0.04)', marginBottom:12 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="#94a3b8"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            <span style={{ fontSize:'0.68rem', fontWeight:700, color:'#64748b', letterSpacing:'0.1em', textTransform:'uppercase' }}>Notre équipe</span>
          </div>
          <h2 style={{ fontSize:'clamp(1.5rem,3vw,2rem)', fontWeight:800, color:'#1e293b', margin:'0 0 8px' }}>Aydees Team</h2>
          <div style={{ width:36, height:2, background:'linear-gradient(90deg,#E8611A,#16a34a)', borderRadius:4, margin:'0 auto 12px' }} />
          <p style={{ color:'#64748b', fontSize:'0.92rem', maxWidth:480, margin:'0 auto' }}>Une équipe d'experts passionnés à votre service</p>
        </div>

        {/* Marquee container */}
        <div style={{ position:'relative', overflow:'hidden' }}>
          <div className="marquee-track" style={{ padding:'12px 0' }}>
            {doubled.map((member, index) => (
              <TeamMember
                key={`${member.id}-${index}`}
                member={member}
                colorIndex={index % teamMembers.length}
              />
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default TeamSection;